import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { assetsOrgAssetsCreate } from '@goldcast/api/content';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import VideoOverlayLibraryModal from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/VideoOverlayLibraryModal';
import { VideoOverlayTrack } from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/types';
import {
  addToRecentlyUsed,
  isCustomUpload
} from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/videoOverlayLibraryUtils';
import { trackClipUpdate } from '@/stores/clip';
import { useMultiAssetUploader } from '@/components/molecules/MultiAssetUploader/useMultiAssetUploader';
import { VIDEO_FORMATS, VIDEO_MAX_SIZE } from '@/components/molecules/MultiAssetUploader/constants';
import type { FileValidationResult, UploadError } from '@/components/molecules/MultiAssetUploader/types';
import { currentUser } from '@/stores/user';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import { updateElementDetailsHandler } from '@/Pages/Clip/ClipPlayer/ClipAssets/handlers';
import { getElementDuration } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ASSET_PATH } from '@/Pages/Clip/ClipPlayer/ClipAssets/constants';
import Icon from '@/components/atoms/Icon';

interface VideoElementReplaceProps {
  selectedElement: VideoElement;
  renderButton?: (handleReplace: (e: React.MouseEvent<Element, MouseEvent>) => void) => React.ReactNode;
}

function VideoElementReplace({ selectedElement, renderButton }: VideoElementReplaceProps) {
  const { clipData, clipId, fps } = useClipsContext();
  const { logger } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const organization = currentUser.getSnapshot()?.organization;
  const baseFilePath = useMemo(() => `${organization}/clip-asset`, [organization]);
  const uploadedUrlRef = useRef<string | undefined>();

  const getCurrentSelectedVideo = useCallback((): VideoOverlayTrack | null => {
    if (!selectedElement?.details?.src) {
      return null;
    }

    try {
      const videoUrl = selectedElement.details.src;
      const videoDuration = selectedElement.details.duration || getElementDuration(selectedElement);

      return {
        id: videoUrl,
        name: selectedElement.name || selectedElement.details.altText || 'Custom Video',
        thumbnailUrl: selectedElement.details.preview || videoUrl,
        videoUrl: videoUrl,
        duration: videoDuration,
        tags: []
      };
    } catch (error) {
      logger.error('Error getting current selected video:', error);
      return null;
    }
  }, [selectedElement, logger]);

  const [currentVideo, setCurrentVideo] = useState<VideoOverlayTrack | null>(getCurrentSelectedVideo());

  useEffect(() => {
    const video = getCurrentSelectedVideo();
    setCurrentVideo(video);
  }, [selectedElement?.details?.src, selectedElement?.name, getCurrentSelectedVideo]);

  const calculateToFrame = useCallback(
    (newDuration: number): number => {
      if (!selectedElement) {
        return 0;
      }
      return selectedElement.fromFrame + newDuration * fps;
    },
    [selectedElement, fps]
  );

  const validateAndGetDuration = useCallback(
    async (videoUrl: string, providedDuration?: number): Promise<number | null> => {
      let duration = providedDuration;

      if (!duration || duration === 0) {
        try {
          duration = await getVideoDurationFromSrc(videoUrl);
        } catch (error) {
          logger.error('Failed to fetch video duration from URL:', error);
          showErrorToast('Failed to fetch video duration');
          return null;
        }
      }

      if (!duration || duration <= 0) {
        logger.error('Invalid video duration:', duration);
        showErrorToast('Invalid video duration');
        return null;
      }

      return duration;
    },
    [logger]
  );

  const handleUploadedAssetsForReplacement = useCallback(
    async (result: FileValidationResult) => {
      if (result.validFiles.length > 0) {
        const uploadedAsset = result.validFiles[0];
        uploadedUrlRef.current = uploadedAsset.url;
        try {
          await assetsOrgAssetsCreate({
            body: [
              {
                name: uploadedAsset.filename,
                source_url: uploadedAsset.url,
                type: 'VIDEO'
              }
            ],
            queryParams: {}
          });

          const newDuration = await validateAndGetDuration(uploadedAsset.url, uploadedAsset.duration);
          if (!newDuration) {
            return;
          }

          const newToFrame = calculateToFrame(newDuration);

          updateElementDetailsHandler(
            clipData,
            clipId,
            selectedElement.id,
            {
              src: uploadedAsset.url,
              preview: undefined,
              altText: uploadedAsset.filename,
              duration: newDuration,
              volume: selectedElement.details.volume,
              muted: selectedElement.details.muted
            },
            { toFrame: newToFrame },
            uploadedAsset.filename
          );

          trackClipUpdate(clipId, 'VIDEO_OVERLAY_REPLACE', {
            replacementSource: 'upload',
            selectedVideoId: `custom-${uploadedAsset.filename.replace(/[^a-zA-Z0-9]/g, '-')}`,
            selectedVideoName: uploadedAsset.filename
          });

          setIsModalOpen(false);
          showSuccessToast('Video replaced successfully');
        } catch (error) {
          logger.error('Failed to replace video:', error);
          showErrorToast('Failed to replace video');
        }
      }
    },
    [selectedElement, clipData, clipId, logger, calculateToFrame, validateAndGetDuration]
  );

  const handleUploadError = useCallback(
    (error: UploadError) => {
      logger.error(error);
      showErrorToast('message' in error ? error.message : 'Failed to upload video');
    },
    [logger]
  );

  const { uploadFiles } = useMultiAssetUploader({
    baseFilePath,
    assetPath: ASSET_PATH,
    formats: VIDEO_FORMATS,
    maxSize: VIDEO_MAX_SIZE,
    handleUploadedAssets: handleUploadedAssetsForReplacement,
    onError: handleUploadError
  });

  const handleModalConfirm = useCallback(
    async (
      selection:
        | { type: 'library'; video: VideoOverlayTrack }
        | { type: 'upload'; file: File }
        | { type: 'recently-used'; video: VideoOverlayTrack }
    ): Promise<string | undefined> => {
      try {
        if (selection.type === 'library' || selection.type === 'recently-used') {
          const videoTrack = selection.video;

          const newDuration = await validateAndGetDuration(videoTrack.videoUrl, videoTrack.duration);
          if (!newDuration) {
            return undefined;
          }

          const newToFrame = calculateToFrame(newDuration);

          updateElementDetailsHandler(
            clipData,
            clipId,
            selectedElement.id,
            {
              src: videoTrack.videoUrl,
              preview: undefined,
              altText: videoTrack.name,
              duration: newDuration,
              volume: selectedElement.details.volume,
              muted: selectedElement.details.muted
            },
            { toFrame: newToFrame },
            videoTrack.name
          );

          addToRecentlyUsed(videoTrack);

          const replacementSource = isCustomUpload(videoTrack) ? 'upload' : 'library';
          trackClipUpdate(clipId, 'VIDEO_OVERLAY_REPLACE', {
            replacementSource,
            selectedVideoId: videoTrack.id,
            selectedVideoName: videoTrack.name
          });

          setIsModalOpen(false);
          showSuccessToast('Video replaced successfully');
          return undefined;
        }

        uploadedUrlRef.current = undefined;
        await uploadFiles([selection.file]);
        return uploadedUrlRef.current;
      } catch (error) {
        logger.error('Failed to handle modal confirm:', error);
        showErrorToast('Failed to replace video');
      }
    },
    [selectedElement, clipData, clipId, logger, calculateToFrame, validateAndGetDuration, uploadFiles]
  );

  const handleReplace = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  }, []);

  return (
    <>
      {renderButton ? (
        renderButton(handleReplace)
      ) : (
        <div className="relative z-10">
          <button
            type="button"
            data-testid="replace-video-button"
            onClick={handleReplace}
            className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
          >
            <Icon name="IconReplace" size={20} />
            <PlayerMenuTooltip content="Replace video" />
          </button>
        </div>
      )}

      {isModalOpen && (
        <VideoOverlayLibraryModal
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleModalConfirm}
          currentVideo={currentVideo}
        />
      )}
    </>
  );
}

export default memo(VideoElementReplace);
